import '../components.scss';
import { Layout, SearchBox, List, Bubble, Button, Segment } from 'tea-component';
import React, { useEffect, useMemo, useState } from 'react';
import './field_select.scss';
import { checkNullString, useDblIntl } from '@/pages/datalab/utils/utils';
import { getFlatOptions } from '../../utils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getLanguage } from '@home/utils/common';

export interface FieldSelectProps {
  fieldValue: string;
  fieldSelected: any[];
  fieldOptions: any[];
  onSelectItem: (fieldValue: string) => void;
  className?: string;
  handleContent?: (option: any) => React.ReactNode;
}

export function FieldSelect(props: FieldSelectProps) {
  const { dblFormatMessage: explorationFormatMessage, commonFormatMessage, isEn } = useDblIntl("exploration");
  const { fieldValue, fieldSelected, fieldOptions, onSelectItem, className = '', handleContent } = props;

  const [searchValue, setSearchValue] = useState('');
  const [selectMenu, setSelectMenu] = useState(fieldOptions?.[0]?.menu_id ?? '');
  const [activeTab, setActiveTab] = useState(fieldOptions?.[0]?.list?.[0]?.tab_id ?? '');
  const [selectMenuItems, activeOptions] = useMemo(() => {
    const menuItems = [...(fieldOptions.find((item) => item.menu_id === selectMenu)?.list ?? [])];
    if (!checkNullString(searchValue)) {
      const tabIndex = menuItems.findIndex((item: any) => item.tab_id === activeTab);
      const searchLower = searchValue.toLowerCase();
      // 搜索是全局搜索，全转小写匹配
      let allOptions = getFlatOptions(fieldOptions);
      allOptions = allOptions.filter(
        (item: any) =>
          (item?.name ?? '').toLowerCase().includes(searchLower) ||
          (item?.name_en ?? '').toLowerCase().includes(searchLower) ||
          (item?.name_zh ?? '').toLowerCase().includes(searchLower)
      );
      if (tabIndex >= 0) {
        menuItems[tabIndex] = {
          ...menuItems[tabIndex],
          options: allOptions,
        };
      }
    }

    let tabItem = menuItems.find((item) => item.tab_id === activeTab);
    if (!tabItem) {
      tabItem = menuItems[0];
    }

    const activeOptions = tabItem?.options ?? [];
    return [menuItems, activeOptions];
  }, [JSON.stringify(fieldOptions), activeTab, selectMenu, searchValue]);

  useEffect(() => {
    const menuItems = [...(fieldOptions.find((item) => item.menu_id === selectMenu)?.list ?? [])];
    setActiveTab(menuItems.find((tab) => tab.tab_id === activeTab) ? activeTab : menuItems?.[0]?.tab_id);
  }, [selectMenu, activeTab]);

  const tabs = useMemo(
    () =>
      selectMenuItems.map((item: any) => ({
        value: item.tab_id,
        text: explorationFormatMessage(`tab_${item.tab_id}`),
      })),
    [JSON.stringify(selectMenuItems), isEn],
  );

  return (
    <Layout className={`drop-down ${className}`}>
      <SearchBox
        placeholder={commonFormatMessage("search")}
        value={searchValue}
        onChange={(value) => setSearchValue(value)}
        className="drop-down-search"
      />
      <Segment
        value={activeTab}
        onChange={(value) => setActiveTab(value)}
        options={tabs}
        className="drop-down-segment"
      />
      <div className="drop-down-context">
        <div className="drop-down-left">
          <List type="option">
            {fieldOptions.map((item: any) => (
              <List.Item
                key={item.menu_id}
                onClick={() => {
                  setSelectMenu(item.menu_id);
                }}
                className={selectMenu == item.menu_id ? 'action-item' : 'item-text'}
              >
                <div className="list-flex">{
                  item?.menu_name_en ? getLanguage(isEn, item.menu_name_en, item.menu_name) :
                    explorationFormatMessage(item.menu_id)
                }</div>
              </List.Item>
            ))}
          </List>
        </div>

        <div className="drop-down-right">
          {/* disable-item 禁用样式 action-item 选中样式 */}
          {activeOptions.length > 0 ? (
            <List type="option">
              {activeOptions.map((option: any) => (
                <List.Item
                  key={option.value}
                  disabled={fieldSelected.includes(option.value)}
                  onClick={() => {
                    onSelectItem(option.value);
                  }}
                  className={
                    fieldValue === option.value
                      ? 'action-item'
                      : fieldSelected.includes(option.value)
                        ? 'disable-item'
                        : 'item-text'
                  }
                >
                  <Bubble
                    content={
                      handleContent ? handleContent?.(option) : (
                        <div>
                          <p>{isEn ? option.name_en : option.name_zh}</p>
                          <p style={{ color: '#bbb', marginTop: '3px' }}>{isEn ? option.desc_en : option.desc}</p>
                          {option.name && <p style={{ marginTop: '13px' }}>{explorationFormatMessage("field_name")}{option.name}</p>}
                          {option.table_name && <p style={{ marginTop: '13px' }}>{explorationFormatMessage("data_table")}{option.table_name}</p>}
                        </div>
                      )
                    }
                    dark={true}
                    placement="right"
                  >
                    <div className="list-flex">
                      <div className="list-flex-text">
                        {isEn ? option.name_en : option.name_zh}
                        {fieldValue === option.value ? <SvgIcon type="action-right" className="list-flex-icon" /> : ''}
                      </div>
                      <div className="list-right">
                        <div className="list-flex-change">{explorationFormatMessage("selected")}</div>
                        {/* 暂时不支持收藏*/}
                        {/* <LikeIncn />*/}
                      </div>
                    </div>
                  </Bubble>
                </List.Item>
              ))}
            </List>
          ) : (
            <div className='drop-down-not-search' style={{ width: 'max-content', height: 'inherit', display: 'flex', lineHeight: '160px', margin: 'auto' }}>
              {commonFormatMessage("not_search")}
              <Button
                type="link"
                style={{ minWidth: 'unset', height: 'unset' }}
                onClick={() => {
                  setSearchValue('');
                }}
              >
                {commonFormatMessage("go_back_old")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const LikeIncn = () => {
  const [change, setChange] = useState(false);
  return (
    <i
      className={change ? 'list-flex-like' : 'list-flex-nolike'}
      onClick={() => {
        setChange(!change);
      }}
    >
      <SvgIcon type={change ? 'star-action' : 'star-m'} />
    </i>
  );
};

const menuObj = (menuId: string, menuName: string) => ({
  menu_id: menuId,
  menu_name: menuName,
  topic: menuName,
  list: [
    tabObj('all', '所有维度'),
    tabObj('frequent', '常用推荐'),
    tabObj('package', '用户包'),
    tabObj('favorite', '我的收藏'),
  ],
});

const tabObj = (tabId: string, tabName: string) => ({
  tab_id: tabId,
  tab_name: tabName,
  options: [],
});

export const defaultMenu = () => [
  menuObj('common', '公共'),
  menuObj('register', '注册'),
  menuObj('active', '活跃'),
  menuObj('pay', '付费'),
  menuObj('exist', '留存'),
  menuObj('gone', '流失'),
];

export const FieldOptions = [
  {
    menu_id: 'loginin',
    menu_name: '登录1',
    list: [
      {
        tab_id: 'all',
        tab_name: '所有维度1',
        options: [
          { value: 'regUser', name: '新进用户' },
          { value: 'regCountry', name: '注册国家' },
          { value: 'regDate', name: '注册时间' },
          { value: 'regChannlid', name: '注册渠道' },
          { value: 'loginTimes', name: '登陆次数' },
          { value: 'loginin_date16', name: '注册gfr时间' },
          { value: 'loginin_date17', name: 'ewea' },
          { value: 'loginin_date18', name: 'lopp' },
          { value: 'loginin_date19', name: 'qet' },
          { value: 'loginin_date101', name: 'nth' },
        ],
      },
      { tab_id: 'frequent', tab_name: '常用推荐', options: [{ value: 'loginin_date2', name: '登录时间' }] },
      { tab_id: 'packages', tab_name: '用户包', options: [{ value: 'loginin_date3', name: '离开时间' }] },
      { tab_id: 'favorite', tab_name: '我的收藏', options: [{ value: 'loginin_date4', name: '的大声点' }] },
    ],
  },

  {
    menu_id: 'pay',
    menu_name: '付费',
    list: [
      { tab_id: 'all', tab_name: '所有维度', options: [{ value: 'pay_date1', name: '注册时间' }] },
      { tab_id: 'frequent', tab_name: '常用推荐', options: [{ value: 'pay_date2', name: '登录时间' }] },
      { tab_id: 'packages', tab_name: '用户包', options: [{ value: 'pay_date3', name: '离开时间' }] },
      { tab_id: 'favorite', tab_name: '我的收藏', options: [{ value: 'pay_date4', name: '的大声点' }] },
    ],
  },

  {
    menu_id: 'loginout',
    menu_name: '登出',
    list: [
      { tab_id: 'all', tab_name: '所有维度', options: [{ value: 'loginout_date1', name: '注册时间' }] },
      { tab_id: 'frequent', tab_name: '常用推荐', options: [{ value: 'loginout_date2', name: '登录时间' }] },
      { tab_id: 'packages', tab_name: '用户包', options: [{ value: 'loginout_date3', name: '离开时间' }] },
      { tab_id: 'favorite', tab_name: '我的收藏', options: [{ value: 'loginout_date4', name: '的大声点' }] },
    ],
  },

  {
    menu_id: 'registry',
    menu_name: '注册',
    list: [
      { tab_id: 'all', tab_name: '所有维度', options: [{ value: 'registry_date1', name: '注册时间' }] },
      { tab_id: 'frequent', tab_name: '常用推荐', options: [{ value: 'registry_date2', name: '登录时间' }] },
      { tab_id: 'packages', tab_name: '用户包', options: [{ value: 'registry_date3', name: '离开时间' }] },
      { tab_id: 'favorite', tab_name: '我的收藏', options: [{ value: 'registry_date4', name: '的大声点' }] },
    ],
  },
];
